import { EventContext } from "Contexts/EventContext";
import { EventMembersContext } from "Contexts/EventMembersContext";
import { MemberContext, currentUserInfo } from "Contexts/MemberContext";
import { addWeeks, format, isAfter, isBefore } from "date-fns";
import ja from "date-fns/locale/ja";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import "./EventList.scss";
import { ParticipantList } from "./ParticipantList";
import { EmailSenderBase } from "../utils/EmailSenderBase";
import { endTimeCalclater } from "../utils/endTimeCalclater";
import { backGroundImage } from "../utils/backGroundImage";

const EventList = () => {
  const [displayImages, setDisplayImages] = useState([]);
  const { eventList } = useContext(EventContext);
  const { eventMembersList } = useContext(EventMembersContext);
  const { membersList, currentUserInfo } = useContext(MemberContext);
  const [events, setEvents] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [eventMembers, setEventMembers] = useState([]);
  const [members, setMembers] = useState([]);
  const [userEventParticipation, setUserEventParticipation] = useState({});
  const [participantCounts, setParticipantCounts] = useState({});
  const navigate = useNavigate();
  const [showSaturdayEvents, setShowSaturdayEvents] = useState(false);
  const [showSundayEvents, setShowSundayEvents] = useState(false);
  const [showWeekdayEvents, setShowWeekdayEvents] = useState(false);
  const [enableJoin, setEnableJoin] = useState(false);
  const [latestEvent, setLatestEvent] = useState(null);
  const [hasAlertBeenShown, setHasAlertBeenShown] = useState(false);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  const [toEmail, setToEmail] = useState([]);
  const [responseMessage, setResponseMessage] = useState("");
  const [eventsWithEndTime, setEventsWithEndTime] = useState([]);

  const userId = currentUser?.uid;
  const useradmin = currentUserInfo?.admin;
  const useradministrator = currentUserInfo?.administrator;

  // ------------------------------
  // 初期データ読み込み
  // ------------------------------
  useEffect(() => {
    const user = auth.currentUser;
    setCurrentUser(user);
    setEvents(eventList || []);
    setEventMembers(eventMembersList || []);
    setMembers(membersList || []);

    if (user) {
      const userId = user.uid;
      const userEventMembers = eventMembersList.filter(
        (member) => member.memberId === userId,
      );
      const userEventIds = userEventMembers.map((member) => member.eventId);
      const userEvents = eventList.filter((event) =>
        userEventIds.includes(event.id),
      );

      setUserEventParticipation(
        userEvents.reduce((acc, event) => {
          acc[event.id] = true;
          return acc;
        }, {}),
      );
    }
  }, [eventList, eventMembersList, membersList]);

  // 管理者メールアドレス抽出
  useEffect(() => {
    const adminmembers = membersList.filter(
      (member) => member.administrator === true,
    );
    const adminEmailString = adminmembers
      .map((member) => member.email)
      .join(", ");
    setToEmail(adminEmailString);
  }, [membersList]);

  // イベントリスト生成とフィルタリング
  useEffect(() => {
    const fetchData = async () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate());
      tomorrow.setHours(0, 0, 0, 0);

      const filteredEventList = eventList.filter(
        (event) => event.starttime >= tomorrow,
      );

      filteredEventList.sort((a, b) => a.starttime - b.starttime);

      const updatedEvents = filteredEventList.map((event) => ({
        ...event,
        endTime: endTimeCalclater(event.id, eventList),
      }));

      setEvents(updatedEvents);

      const user = auth.currentUser;
      setCurrentUser(user);

      if (user) {
        const userId = user.uid;
        const userEventMembers = eventMembersList.filter(
          (member) => member.memberId === userId,
        );
        const userEventIds = userEventMembers.map((member) => member.eventId);
        const userEvents = filteredEventList.filter((event) =>
          userEventIds.includes(event.id),
        );

        const mostFutureEvent = userEvents.reduce((latest, event) => {
          return !latest || isAfter(event.starttime, latest.starttime)
            ? event
            : latest;
        }, null);

        setLatestEvent(mostFutureEvent);

        const userParticipation = userEventMembers.reduce((acc, member) => {
          acc[member.eventId] = true;
          return acc;
        }, {});
        setUserEventParticipation(userParticipation);

        const filteredMembers = membersList.filter(
          (member) => member.author.id === userId,
        );
        if (filteredMembers.length === 0) {
          alert(
            "あなたはメンバー名が未登録です。メンバー登録を行ってください。",
          );
          navigate("/membercreate");
        }
      }
    };

    fetchData();
  }, [navigate, eventList, eventMembersList, membersList]);

  // 参加人数をカウント
  useEffect(() => {
    const fetchParticipantCounts = () => {
      const counts = events.reduce((acc, event) => {
        const participants = eventMembers.filter(
          (member) => member.eventId === event.id,
        );
        acc[event.id] = participants.length;
        return acc;
      }, {});
      setParticipantCounts(counts);
    };
    fetchParticipantCounts();
  }, [events, eventMembers]);

  // ------------------------------
  // 参加ボタン処理（改良版）
  // ------------------------------
  const handleJoinEvent = async (eventId) => {
    if (!currentUserInfo) {
      alert("会員登録および管理者認定をお願いいたします");
      navigate("/login");
      return;
    }

    if (currentUserInfo.admin === false) {
      alert("管理者による認証がまだなされていません。");
      return;
    }

    if (!enableJoin) {
      alert("参加ボタン有効化または確認アラート不要をチェックしてください。");
      return;
    }

    const event = events.find((e) => e.id === eventId);
    if (!event) {
      alert("イベントが見つかりません");
      return;
    }

    if (!skipConfirmation) {
      const confirmMessage = `${format(event.starttime, "M月d日 (E) H:mm", {
        locale: ja,
      })} のイベントに本当に参加しますか？`;
      if (!window.confirm(confirmMessage)) return;
    }

    const currentParticipantsCount = participantCounts[eventId] || 0;
    if (currentParticipantsCount >= event.capacity) {
      alert("定員締め切りのため参加できません。");
      return;
    }

    const user = members.find((member) => member.author.id === currentUser.uid);
    if (!user) {
      alert("メンバー名が未登録です。メンバー登録を行ってください。");
      navigate("/member");
      return;
    }

    const accountname = user.accountname;
    const existingMember = eventMembers.find(
      (member) =>
        member.eventId === eventId && member.memberId === currentUser.uid,
    );
    if (existingMember) {
      alert("すでに参加申込しています。");
      return;
    }

    // Firestoreに追加
    const docRef = await addDoc(collection(db, "event_members"), {
      eventId,
      memberId: currentUser.uid,
      accountname,
      eventdate: event.starttime,
      useradmin,
      useradministrator,
      created_at: new Date(),
      updated_at: new Date(),
    });

    await addDoc(collection(doc(db, "events", eventId), "members"), {
      memberId: userId,
      accountname,
      useradmin,
      useradministrator,
      created_at: new Date(),
      updated_at: new Date(),
    });

    const docId = docRef.id;

    // ✅ ローカル更新（二重表示防止付き）
    setEventMembers((prevEventMembers) => {
      const exists = prevEventMembers.some(
        (member) =>
          member.eventId === eventId && member.memberId === currentUser.uid,
      );
      if (exists) return prevEventMembers;
      return [
        ...prevEventMembers,
        { eventId, memberId: currentUser.uid, id: docId },
      ];
    });

    // ✅ 参加予定ステート即更新（iPhone対応）
    setTimeout(() => {
      setUserEventParticipation((prevParticipation) => ({
        ...prevParticipation,
        [eventId]: true,
      }));
    }, 0);
  };

  // ------------------------------
  // イベント削除処理
  // ------------------------------
  const handleDelete = async (eventId) => {
    const deleteEvent = events.filter((event) => event.id === eventId);
    const dateObject = new Date(deleteEvent[0].starttime);
    const month = dateObject.getMonth() + 1;
    const day = dateObject.getDate();
    const confirmDelete = window.confirm(
      `本当に${month}月${day}日の${deleteEvent[0].title}を削除しますか？`,
    );

    if (!confirmDelete) return;
    try {
      const batch = writeBatch(db);
      const eventRef = doc(db, "events", eventId);
      batch.delete(eventRef);

      const eventMembersQuery = query(
        collection(db, "event_members"),
        where("eventId", "==", eventId),
      );
      const eventMembersSnapshot = await getDocs(eventMembersQuery);
      eventMembersSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log("イベントと関連する参加者情報が削除されました");
    } catch (error) {
      console.error("削除中にエラーが発生しました:", error);
    }
  };

  const isAdmin = currentUserInfo?.administrator === true;

  const handleWeekdayCheckboxChange = () =>
    setShowWeekdayEvents(!showWeekdayEvents);
  const handleSaturdayCheckboxChange = () =>
    setShowSaturdayEvents(!showSaturdayEvents);
  const handleSundayCheckboxChange = () =>
    setShowSundayEvents(!showSundayEvents);

  // アラート
  useEffect(() => {
    if (latestEvent) {
      const now = new Date();
      const threeWeeksFromNow = addWeeks(now, 3);
      if (
        isBefore(latestEvent.starttime, threeWeeksFromNow) &&
        !hasAlertBeenShown
      ) {
        alert(
          "テニスオフでの参加募集は２週間前開始です。できる限りそれまでに申し込みをお願いします。",
        );
        setHasAlertBeenShown(true);
      }
    }
  }, [latestEvent, hasAlertBeenShown]);

  useEffect(() => {
    backGroundImage(setDisplayImages);
  }, []);

  const displayImage = displayImages.length > 0 ? displayImages[0].src : "";
  // ------------------------------
  // 曜日フィルタ
  // ------------------------------
  const filteredEvents = events.filter((event) => {
    const day = format(event.starttime, "E", { locale: ja });

    // 何も選択されていない → 全表示
    if (!showSaturdayEvents && !showSundayEvents && !showWeekdayEvents) {
      return true;
    }

    if (day === "土" && showSaturdayEvents) return true;
    if (day === "日" && showSundayEvents) return true;

    // 平日（月〜金）
    if (day !== "土" && day !== "日" && showWeekdayEvents) return true;

    return false;
  });
  // ------------------------------
  // JSX（表示部分）
  // ------------------------------
  return (
    <div
      className="eventlist-container"
      style={{ backgroundImage: `url(${displayImage})` }}
    >
      <div className={`eventlist-content ${isAdmin ? "" : "member"}`}>
        <div className="eventList-lable">
          <h1>
            <span className="eventlist-top-title">イベント一覧</span>
          </h1>
          <img
            src={"/images/calendar.png"}
            alt="カレンダー"
            className="calendar-image"
            onClick={() => navigate(`/calendar`)}
          />
        </div>

        <div className="eventlist-title">
          <div className="eventlist-filter">
            <div className="eventlist-filter-in">
              <h3>表示する曜日を選択</h3>
              <label>
                <input
                  type="checkbox"
                  checked={showSaturdayEvents}
                  onChange={handleSaturdayCheckboxChange}
                />
                土
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showSundayEvents}
                  onChange={handleSundayCheckboxChange}
                />
                日
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showWeekdayEvents}
                  onChange={handleWeekdayCheckboxChange}
                />
                平日
              </label>
            </div>
            <div className="eventlist-filter-in2">
              <label>
                <input
                  type="checkbox"
                  checked={enableJoin}
                  onChange={() => setEnableJoin(!enableJoin)}
                />
                参加ボタン有効化
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={skipConfirmation}
                  onChange={() => {
                    setSkipConfirmation(!skipConfirmation);
                    setEnableJoin(!enableJoin);
                  }}
                />
                確認アラート不要
              </label>
            </div>
          </div>
        </div>

        {/* イベントテーブル */}
        <table>
          <thead>
            <tr>
              <th>開催日時</th>
              <th className="hide-on-narrow">開催時間</th>
              <th>タイトル</th>
              <th className="hide-on-narrow">開催場所</th>
              <th className="hide-on-narrow">定員</th>
              <th className="hide-on-narrow">参加者</th>
              <th>参加</th>
              {isAdmin && <th className="hide-on-narrow">削除</th>}
              {isAdmin && <th className="hide-on-narrow">編集</th>}
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((event, index) => {
              const dayOfWeek = format(event.starttime, "E", { locale: ja });
              const dayStyle = {
                color:
                  dayOfWeek === "土"
                    ? "blue"
                    : dayOfWeek === "日"
                      ? "red"
                      : "black",
              };

              return (
                <tr key={index}>
                  <td
                    className="event_button_title"
                    onClick={() => navigate(`/eventdetail/${event.id}`)}
                  >
                    {format(event.starttime, "M /d(", { locale: ja })}
                    <span style={dayStyle}>
                      {format(event.starttime, "E", { locale: ja })}
                    </span>
                    )
                    <span className="hide-on-narrow">
                      <br />　{format(event.starttime, "a", { locale: ja })}
                      {format(event.starttime, "h時", { locale: ja })}～{" "}
                    </span>
                  </td>

                  <td className="hide-on-narrow">
                    {event.duration_hour > 0
                      ? `${parseInt(event.duration_hour)} 時間 `
                      : ""}
                    {event.duration_minute > 0
                      ? `${event.duration_minute} 分`
                      : ""}
                  </td>

                  <td
                    className="event_button_title"
                    onClick={() => navigate(`/eventdetail/${event.id}`)}
                  >
                    {event.title}
                  </td>
                  <td className="hide-on-narrow">{event.site_region}</td>
                  <td className="hide-on-narrow">{event.capacity}</td>
                  <td className="hide-on-narrow">
                    <div className="participantList">
                      <ParticipantList
                        eventId={event.id}
                        eventMembers={eventMembers}
                        members={members}
                        navigate={navigate}
                        administrator={isAdmin}
                        currentUser={currentUser}
                      />
                    </div>
                  </td>

                  <td>
                    {userEventParticipation[event.id] ? (
                      <button className="join-button">参加予定</button>
                    ) : participantCounts[event.id] >= event.capacity ? (
                      <button
                        className="limit-button"
                        disabled
                        style={{
                          cursor: "not-allowed",
                          backgroundColor: "#a99fe9",
                        }}
                      >
                        定員締切
                      </button>
                    ) : (
                      <button
                        className="aplay-button"
                        onClick={() => handleJoinEvent(event.id)}
                        style={{ backgroundColor: "#ffcc6f" }}
                      >
                        募集中
                      </button>
                    )}
                  </td>

                  {isAdmin && (
                    <td className="hide-on-narrow">
                      <button
                        className="eventlist-delete-button"
                        onClick={() => handleDelete(event.id)}
                      >
                        削除
                      </button>
                    </td>
                  )}
                  {isAdmin && (
                    <td className="hide-on-narrow">
                      <button
                        className="eventlist-edit-button"
                        onClick={() => navigate(`/eventinfo/${event.id}/edit`)}
                      >
                        編集
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventList;
